import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import {
  fetchBatchRecordIngredientsByBatchRecordId,
  createBatchRecordIngredient,
  updateBatchRecordIngredient,
  deleteBatchRecordIngredient
} from '../../../store/slices/batchRecordIngredientsSlice';
import { fetchFillingBatches } from '../../../store/slices/fillingBatchSlice';
import {
  fetchBatchRecordFillingBatchesByBatchRecordId,
  createBatchRecordFillingBatch,
  deleteBatchRecordFillingBatch
} from '../../../store/slices/batchRecordFillingBatchSlice';
import { fetchDoughBatches } from '../../../store/slices/doughBatchSlice';
import {
  fetchBatchRecordDoughBatchesByBatchRecordId,
  createBatchRecordDoughBatch,
  deleteBatchRecordDoughBatch
} from '../../../store/slices/batchRecordDoughBatchSlice';
import { fillingBatchIngredientApiService } from '../../../services/fillingBatchIngredientApiService';
import { fillingBatchComponentBatchApiService } from '../../../services/fillingBatchComponentBatchApiService';
import { componentBatchIngredientApiService } from '../../../services/componentBatchIngredientApiService';
import { doughBatchIngredientApiService } from '../../../services/doughBatchIngredientApiService';
import { BatchRecordIngredient } from '../../../types/batchRecordIngredient';
import { FillingBatchIngredient } from '../../../types/fillingBatchIngredient';
import { ComponentBatchIngredient } from '../../../types/componentBatchIngredient';
import { DoughBatchIngredient } from '../../../types/doughBatchIngredient';
import { BatchRecordIngredientRow } from './BatchRecordIngredientRow';
import { BatchRecordIngredientModal } from './BatchRecordIngredientModal';

interface BatchRecordIngredientsTableProps {
  batchRecordId: string;
}

export const BatchRecordIngredientsTable: React.FC<BatchRecordIngredientsTableProps> = ({
  batchRecordId
}) => {
  const dispatch = useAppDispatch();
  const batchRecordIngredients = useAppSelector(state =>
    state.batchRecordIngredients.ingredients.filter(
      ing => ing.batchRecordId === batchRecordId
    )
  );
  const rawIngredients = useAppSelector(state => state.rawIngredients.ingredients);
  const traceCodes = useAppSelector(state => state.ingredientTraceCodes.traceCodes);
  const loading = useAppSelector(state => state.batchRecordIngredients.loading);
  const error = useAppSelector(state => state.batchRecordIngredients.error);

  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState<BatchRecordIngredient | null>(null);

  // New ingredient state for inline adding
  const [newIngredientName, setNewIngredientName] = useState<string | null>(null);
  const [newLotNumber, setNewLotNumber] = useState<string | null>(null);
  const [newQuantity, setNewQuantity] = useState<string>('');
  const [newUnit, setNewUnit] = useState('');

  // Filling batch selector state
  const [isAddingFillingBatch, setIsAddingFillingBatch] = useState(false);
  const [selectedFillingBatchIds, setSelectedFillingBatchIds] = useState<string[]>([]);

  // Dough batch selector state
  const [isAddingDoughBatch, setIsAddingDoughBatch] = useState(false);
  const [selectedDoughBatchIds, setSelectedDoughBatchIds] = useState<string[]>([]);

  const fillingBatches = useAppSelector(state => state.fillingBatches.items);
  const batchRecordFillingBatches = useAppSelector(state =>
    state.batchRecordFillingBatches.items.filter(
      brfb => brfb.batchRecordId === batchRecordId
    )
  );
  const fillingBatchesLoading = useAppSelector(state => state.fillingBatches.loading);

  const doughBatches = useAppSelector(state => state.doughBatches.items);
  const batchRecordDoughBatches = useAppSelector(state =>
    state.batchRecordDoughBatches.items.filter(
      brdb => brdb.batchRecordId === batchRecordId
    )
  );
  const doughBatchesLoading = useAppSelector(state => state.doughBatches.loading);

  useEffect(() => {
    console.log('[BatchRecordIngredientsTable] Component mounted, fetching data for batchRecordId:', batchRecordId);
    dispatch(fetchBatchRecordIngredientsByBatchRecordId(batchRecordId));
    dispatch(fetchFillingBatches());
    dispatch(fetchBatchRecordFillingBatchesByBatchRecordId(batchRecordId));
    dispatch(fetchDoughBatches());
    dispatch(fetchBatchRecordDoughBatchesByBatchRecordId(batchRecordId));
  }, [dispatch, batchRecordId]);

  // Debug effect to log when data changes
  useEffect(() => {
    console.log('[BatchRecordIngredientsTable] Data updated:');
    console.log('[BatchRecordIngredientsTable] - rawIngredients count:', rawIngredients.length);
    console.log('[BatchRecordIngredientsTable] - traceCodes count:', traceCodes.length);
    console.log('[BatchRecordIngredientsTable] - batchRecordIngredients count:', batchRecordIngredients.length);
  }, [rawIngredients, traceCodes, batchRecordIngredients]);

  // Debug effect to log form state changes
  useEffect(() => {
    console.log('[BatchRecordIngredientsTable] Form state changed:');
    console.log('[BatchRecordIngredientsTable] - newIngredientName:', newIngredientName);
    console.log('[BatchRecordIngredientsTable] - newLotNumber:', newLotNumber);
    console.log('[BatchRecordIngredientsTable] - newQuantity:', newQuantity);
    console.log('[BatchRecordIngredientsTable] - newUnit:', newUnit);
    console.log('[BatchRecordIngredientsTable] - isAddingNew:', isAddingNew);
  }, [newIngredientName, newLotNumber, newQuantity, newUnit, isAddingNew]);

  // Helper function to get unique ingredient names
  const getUniqueIngredientNames = (): string[] => {
    const names = new Set(rawIngredients.map(ing => ing.name));
    return Array.from(names).sort();
  };

  // Helper function to determine rawIngredientId from selected lot
  const getRawIngredientIdForLot = (lotNumber: string): string | null => {
    // Find the trace code
    const traceCode = traceCodes.find(tc => tc.internalTraceCode === lotNumber);
    if (!traceCode) {
      console.log('[BatchRecordIngredientsTable] Trace code not found for lot:', lotNumber);
      return null;
    }

    // Find the raw ingredient with matching receivingLogId
    const rawIngredient = rawIngredients.find(
      ing => ing.receivingLogId === traceCode.ingredientReceivingId
    );

    if (!rawIngredient) {
      console.log('[BatchRecordIngredientsTable] Raw ingredient not found for trace code:', traceCode);
      return null;
    }

    console.log('[BatchRecordIngredientsTable] Found raw ingredient:', rawIngredient.name, rawIngredient.id);
    return rawIngredient.id;
  };

  const handleAddIngredient = () => {
    setIsAddingNew(true);
    setNewIngredientName(null);
    setNewLotNumber(null);
    setNewQuantity('');
    setNewUnit('');
  };

  const handleCancelAdd = () => {
    setIsAddingNew(false);
    setNewIngredientName(null);
    setNewLotNumber(null);
    setNewQuantity('');
    setNewUnit('');
  };

  const handleSaveNew = async () => {
    console.log('[BatchRecordIngredientsTable] === SAVE VALIDATION DEBUG ===');
    console.log('[BatchRecordIngredientsTable] newIngredientName:', newIngredientName);
    console.log('[BatchRecordIngredientsTable] newLotNumber:', newLotNumber);
    console.log('[BatchRecordIngredientsTable] newQuantity:', newQuantity);
    console.log('[BatchRecordIngredientsTable] newUnit:', newUnit);

    // Check required fields only
    const checks = {
      hasIngredientName: !!newIngredientName,
      hasLotNumber: !!newLotNumber
    };

    console.log('[BatchRecordIngredientsTable] Validation checks:', checks);

    // Identify failing fields
    const failingFields = [];
    if (!checks.hasIngredientName) failingFields.push('Ingredient Name');
    if (!checks.hasLotNumber) failingFields.push('Lot Number');

    if (failingFields.length > 0) {
      console.error('[BatchRecordIngredientsTable] ❌ Validation FAILED. Missing fields:', failingFields);
      alert(`Please fill in required fields: ${failingFields.join(', ')}`);
      return;
    }

    // Validate quantity format if provided
    let quantityValue: number | undefined = undefined;
    if (newQuantity.trim() !== '') {
      quantityValue = parseFloat(newQuantity);
      if (isNaN(quantityValue) || quantityValue <= 0) {
        console.error('[BatchRecordIngredientsTable] ❌ Invalid quantity value');
        alert('Please enter a valid quantity greater than 0, or leave it empty');
        return;
      }
    }

    // Determine the rawIngredientId from the selected lot
    const rawIngredientId = getRawIngredientIdForLot(newLotNumber!);
    if (!rawIngredientId) {
      console.error('[BatchRecordIngredientsTable] ❌ Could not determine raw ingredient ID for lot:', newLotNumber);
      alert('Could not determine ingredient. Please try again.');
      return;
    }

    console.log('[BatchRecordIngredientsTable] ✓ Validation PASSED. Proceeding with save...');

    try {
      await dispatch(
        createBatchRecordIngredient({
          batchRecordId: batchRecordId,
          rawIngredientId: rawIngredientId,
          lotNumbers: [newLotNumber!],
          quantity: quantityValue,
          unit: newUnit.trim() !== '' ? newUnit : undefined
        })
      ).unwrap();
      console.log('[BatchRecordIngredientsTable] ✓ Ingredient saved successfully');
      handleCancelAdd();
    } catch (err) {
      console.error('[BatchRecordIngredientsTable] ❌ Failed to create ingredient:', err);
      alert('Failed to add ingredient. Please try again.');
    }
  };

  const handleEditIngredient = (ingredient: BatchRecordIngredient) => {
    setEditingIngredient(ingredient);
  };

  const handleCloseModal = () => {
    setEditingIngredient(null);
  };

  const handleSaveEdit = async (updatedData: {
    rawIngredientId: string;
    lotNumbers: string[];
    quantity?: number;
    unit?: string;
  }) => {
    if (!editingIngredient) return;

    try {
      await dispatch(
        updateBatchRecordIngredient({
          id: editingIngredient.id,
          data: updatedData
        })
      ).unwrap();
      setEditingIngredient(null);
    } catch (err) {
      console.error('Failed to update ingredient:', err);
      alert('Failed to update ingredient. Please try again.');
    }
  };

  const handleDelete = async (id: string, ingredientName: string) => {
    if (window.confirm(`Are you sure you want to delete "${ingredientName}"?`)) {
      try {
        await dispatch(deleteBatchRecordIngredient(id)).unwrap();
      } catch (err) {
        console.error('Failed to delete ingredient:', err);
        alert('Failed to delete ingredient. Please try again.');
      }
    }
  };

  const getAvailableLots = (ingredientName: string | null): string[] => {
    console.log('[BatchRecordIngredientsTable] getAvailableLots called with ingredientName:', ingredientName);

    if (!ingredientName) {
      console.log('[BatchRecordIngredientsTable] No ingredientName provided, returning empty array');
      return [];
    }

    // Find all raw ingredients with this name
    const matchingIngredients = rawIngredients.filter(ing => ing.name === ingredientName);
    console.log('[BatchRecordIngredientsTable] Found matching ingredients count:', matchingIngredients.length);

    if (matchingIngredients.length === 0) {
      console.log('[BatchRecordIngredientsTable] No matching ingredients found, returning empty array');
      return [];
    }

    // Get all receiving log IDs
    const receivingLogIds = matchingIngredients.map(ing => ing.receivingLogId);
    console.log('[BatchRecordIngredientsTable] Receiving log IDs:', receivingLogIds);

    // Get all trace codes for these receiving logs
    const ingredientTraceCodes = traceCodes.filter(
      tc => receivingLogIds.includes(tc.ingredientReceivingId || '')
    );

    console.log('[BatchRecordIngredientsTable] Found trace codes count:', ingredientTraceCodes.length);
    console.log('[BatchRecordIngredientsTable] Trace codes:', ingredientTraceCodes.map(tc => tc.internalTraceCode));

    // Return unique internal trace codes
    const lots = Array.from(new Set(ingredientTraceCodes.map(tc => tc.internalTraceCode)));
    console.log('[BatchRecordIngredientsTable] Returning unique lots:', lots);
    return lots;
  };

  // Utility function to combine ingredients from multiple filling batches and component batches
  interface CombinedIngredient {
    rawIngredientId: string;
    lotNumbers: string[];
    quantity: number | null;
    unit: string | null;
  }

  const combineIngredients = (ingredients: (FillingBatchIngredient | ComponentBatchIngredient)[]): CombinedIngredient[] => {
    const grouped = new Map<string, CombinedIngredient>();

    for (const ingredient of ingredients) {
      const existing = grouped.get(ingredient.rawIngredientId);

      if (existing) {
        // Combine lot numbers (unique only)
        const allLots = [...existing.lotNumbers, ...ingredient.lotNumbers];
        existing.lotNumbers = Array.from(new Set(allLots));

        // Sum quantities if both have values and same unit
        if (existing.quantity !== null && ingredient.quantity !== null) {
          if (existing.unit === ingredient.unit) {
            existing.quantity += ingredient.quantity;
          } else {
            // Different units - keep first, log warning
            console.warn(`Cannot combine quantities with different units: ${existing.unit} vs ${ingredient.unit}`);
          }
        } else if (ingredient.quantity !== null) {
          existing.quantity = ingredient.quantity;
          existing.unit = ingredient.unit;
        }
      } else {
        grouped.set(ingredient.rawIngredientId, {
          rawIngredientId: ingredient.rawIngredientId,
          lotNumbers: [...ingredient.lotNumbers],
          quantity: ingredient.quantity,
          unit: ingredient.unit
        });
      }
    }

    return Array.from(grouped.values());
  };

  // Handlers for filling batch selection
  const handleFillingBatchToggle = (fillingBatchId: string, isChecked: boolean) => {
    if (isChecked) {
      setSelectedFillingBatchIds([...selectedFillingBatchIds, fillingBatchId]);
    } else {
      setSelectedFillingBatchIds(selectedFillingBatchIds.filter(id => id !== fillingBatchId));
    }
  };

  const handleAddSelectedFillingBatches = async () => {
    if (selectedFillingBatchIds.length === 0) {
      alert('Please select at least one filling batch');
      return;
    }

    try {
      // 1. Create junction table entries
      for (const fillingBatchId of selectedFillingBatchIds) {
        await dispatch(createBatchRecordFillingBatch({
          batchRecordId,
          fillingBatchId
        })).unwrap();
      }

      // 2. Fetch all ingredients from selected filling batches (direct ingredients AND component batch ingredients)
      const allIngredients: (FillingBatchIngredient | ComponentBatchIngredient)[] = [];

      for (const fillingBatchId of selectedFillingBatchIds) {
        // 2a. Fetch direct filling batch ingredients
        const fillingIngredients = await fillingBatchIngredientApiService
          .fetchFillingBatchIngredientsByFillingBatchId(fillingBatchId);
        allIngredients.push(...fillingIngredients);

        console.log(`[BatchRecordIngredientsTable] Fetched ${fillingIngredients.length} direct ingredients for filling batch ${fillingBatchId}`);

        // 2b. Fetch component batches linked to this filling batch
        const componentBatches = await fillingBatchComponentBatchApiService
          .fetchFillingBatchComponentBatchesByFillingBatchId(fillingBatchId);

        console.log(`[BatchRecordIngredientsTable] Found ${componentBatches.length} component batches for filling batch ${fillingBatchId}`);

        // 2c. For each component batch, fetch its ingredients
        for (const componentBatch of componentBatches) {
          const componentIngredients = await componentBatchIngredientApiService
            .fetchComponentBatchIngredientsByComponentBatchId(componentBatch.componentBatchId);
          allIngredients.push(...componentIngredients);

          console.log(`[BatchRecordIngredientsTable] Fetched ${componentIngredients.length} ingredients from component batch ${componentBatch.componentBatchId}`);
        }
      }

      console.log(`[BatchRecordIngredientsTable] Total ingredients collected (before combining): ${allIngredients.length}`);

      // 3. Combine duplicate ingredients (same rawIngredientId)
      const combinedIngredients = combineIngredients(allIngredients);

      console.log(`[BatchRecordIngredientsTable] Combined ingredients count: ${combinedIngredients.length}`);

      // 4. Create batch_record_ingredients entries
      for (const ingredient of combinedIngredients) {
        await dispatch(createBatchRecordIngredient({
          batchRecordId,
          rawIngredientId: ingredient.rawIngredientId,
          lotNumbers: ingredient.lotNumbers,
          quantity: ingredient.quantity ?? undefined,
          unit: ingredient.unit ?? undefined
        })).unwrap();
      }

      console.log(`[BatchRecordIngredientsTable] Successfully created ${combinedIngredients.length} batch record ingredients`);

      // Reset state
      setSelectedFillingBatchIds([]);
      setIsAddingFillingBatch(false);
    } catch (err) {
      console.error('Failed to add filling batches:', err);
      alert('Failed to add filling batches. Please try again.');
    }
  };

  const handleRemoveFillingBatch = async (fillingBatchId: string) => {
    if (!window.confirm('Remove this filling batch? This will also remove its ingredients and component batch ingredients from the batch record.')) {
      return;
    }

    try {
      // 1. Find the junction record
      const junctionRecord = batchRecordFillingBatches.find(
        jb => jb.batchRecordId === batchRecordId && jb.fillingBatchId === fillingBatchId
      );

      if (!junctionRecord) return;

      // 2. Delete junction record
      await dispatch(deleteBatchRecordFillingBatch(junctionRecord.id)).unwrap();

      // 3. Collect all ingredients to remove (filling + component batch ingredients)
      const allIngredientsToRemove: (FillingBatchIngredient | ComponentBatchIngredient)[] = [];

      // 3a. Fetch direct filling batch ingredients
      const fillingIngredients = await fillingBatchIngredientApiService
        .fetchFillingBatchIngredientsByFillingBatchId(fillingBatchId);
      allIngredientsToRemove.push(...fillingIngredients);

      console.log(`[BatchRecordIngredientsTable] Removing ${fillingIngredients.length} direct filling ingredients`);

      // 3b. Fetch component batches and their ingredients
      const componentBatches = await fillingBatchComponentBatchApiService
        .fetchFillingBatchComponentBatchesByFillingBatchId(fillingBatchId);

      console.log(`[BatchRecordIngredientsTable] Found ${componentBatches.length} component batches to remove ingredients from`);

      for (const componentBatch of componentBatches) {
        const componentIngredients = await componentBatchIngredientApiService
          .fetchComponentBatchIngredientsByComponentBatchId(componentBatch.componentBatchId);
        allIngredientsToRemove.push(...componentIngredients);

        console.log(`[BatchRecordIngredientsTable] Removing ${componentIngredients.length} ingredients from component batch ${componentBatch.componentBatchId}`);
      }

      console.log(`[BatchRecordIngredientsTable] Total ingredients to remove: ${allIngredientsToRemove.length}`);

      // 4. Delete matching batch record ingredients
      for (const ingredientToRemove of allIngredientsToRemove) {
        const matchingBatchIngredient = batchRecordIngredients.find(
          bIng => bIng.rawIngredientId === ingredientToRemove.rawIngredientId &&
                  JSON.stringify(bIng.lotNumbers.sort()) === JSON.stringify(ingredientToRemove.lotNumbers.sort())
        );

        if (matchingBatchIngredient) {
          await dispatch(deleteBatchRecordIngredient(matchingBatchIngredient.id)).unwrap();
          console.log(`[BatchRecordIngredientsTable] Deleted batch record ingredient: ${matchingBatchIngredient.rawIngredientName}`);
        }
      }

      console.log('[BatchRecordIngredientsTable] Successfully removed filling batch and all its ingredients');
    } catch (err) {
      console.error('Failed to remove filling batch:', err);
      alert('Failed to remove filling batch. Please try again.');
    }
  };

  // Handlers for dough batch selection
  const handleDoughBatchToggle = (doughBatchId: string, isChecked: boolean) => {
    if (isChecked) {
      setSelectedDoughBatchIds([...selectedDoughBatchIds, doughBatchId]);
    } else {
      setSelectedDoughBatchIds(selectedDoughBatchIds.filter(id => id !== doughBatchId));
    }
  };

  const handleAddSelectedDoughBatches = async () => {
    if (selectedDoughBatchIds.length === 0) {
      alert('Please select at least one dough batch');
      return;
    }

    try {
      // 1. Create junction table entries
      for (const doughBatchId of selectedDoughBatchIds) {
        await dispatch(createBatchRecordDoughBatch({
          batchRecordId,
          doughBatchId
        })).unwrap();
      }

      // 2. Fetch all ingredients from selected dough batches
      const allIngredients: (FillingBatchIngredient | ComponentBatchIngredient)[] = [];

      for (const doughBatchId of selectedDoughBatchIds) {
        const doughIngredients = await doughBatchIngredientApiService
          .fetchDoughBatchIngredientsByDoughBatchId(doughBatchId);

        console.log(`[BatchRecordIngredientsTable] Fetched ${doughIngredients.length} ingredients for dough batch ${doughBatchId}`);

        // Convert dough batch ingredients to the expected format
        // DoughBatchIngredient has lotNumber (string) while others have lotNumbers (array)
        for (const doughIng of doughIngredients) {
          if (doughIng.rawIngredientId) {
            allIngredients.push({
              rawIngredientId: doughIng.rawIngredientId,
              lotNumbers: doughIng.lotNumber ? [doughIng.lotNumber] : [],
              quantity: doughIng.quantity,
              unit: doughIng.unit
            } as FillingBatchIngredient);
          }
        }
      }

      console.log(`[BatchRecordIngredientsTable] Total dough ingredients collected (before combining): ${allIngredients.length}`);

      // 3. Combine duplicate ingredients (same rawIngredientId)
      const combinedIngredients = combineIngredients(allIngredients);

      console.log(`[BatchRecordIngredientsTable] Combined dough ingredients count: ${combinedIngredients.length}`);

      // 4. Create batch_record_ingredients entries
      for (const ingredient of combinedIngredients) {
        await dispatch(createBatchRecordIngredient({
          batchRecordId,
          rawIngredientId: ingredient.rawIngredientId,
          lotNumbers: ingredient.lotNumbers,
          quantity: ingredient.quantity ?? undefined,
          unit: ingredient.unit ?? undefined
        })).unwrap();
      }

      console.log(`[BatchRecordIngredientsTable] Successfully created ${combinedIngredients.length} batch record ingredients from dough batches`);

      // Reset state
      setSelectedDoughBatchIds([]);
      setIsAddingDoughBatch(false);
    } catch (err) {
      console.error('Failed to add dough batches:', err);
      alert('Failed to add dough batches. Please try again.');
    }
  };

  const handleRemoveDoughBatch = async (doughBatchId: string) => {
    if (!window.confirm('Remove this dough batch? This will also remove its ingredients from the batch record.')) {
      return;
    }

    try {
      // 1. Find the junction record
      const junctionRecord = batchRecordDoughBatches.find(
        jb => jb.batchRecordId === batchRecordId && jb.doughBatchId === doughBatchId
      );

      if (!junctionRecord) return;

      // 2. Delete junction record
      await dispatch(deleteBatchRecordDoughBatch(junctionRecord.id)).unwrap();

      // 3. Fetch dough batch ingredients to remove
      const doughIngredients = await doughBatchIngredientApiService
        .fetchDoughBatchIngredientsByDoughBatchId(doughBatchId);

      console.log(`[BatchRecordIngredientsTable] Removing ${doughIngredients.length} dough ingredients`);

      // 4. Delete matching batch record ingredients
      for (const doughIng of doughIngredients) {
        if (!doughIng.rawIngredientId) continue;

        const lotNumbers = doughIng.lotNumber ? [doughIng.lotNumber] : [];
        const matchingBatchIngredient = batchRecordIngredients.find(
          bIng => bIng.rawIngredientId === doughIng.rawIngredientId &&
                  JSON.stringify(bIng.lotNumbers.sort()) === JSON.stringify(lotNumbers.sort())
        );

        if (matchingBatchIngredient) {
          await dispatch(deleteBatchRecordIngredient(matchingBatchIngredient.id)).unwrap();
          console.log(`[BatchRecordIngredientsTable] Deleted batch record ingredient: ${matchingBatchIngredient.rawIngredientName}`);
        }
      }

      console.log('[BatchRecordIngredientsTable] Successfully removed dough batch and its ingredients');
    } catch (err) {
      console.error('Failed to remove dough batch:', err);
      alert('Failed to remove dough batch. Please try again.');
    }
  };

  // Get available filling batches (exclude already selected ones)
  const availableFillingBatches = fillingBatches.filter(
    fb => !batchRecordFillingBatches.some(brfb => brfb.fillingBatchId === fb.id)
  );

  // Get available dough batches (exclude already selected ones)
  const availableDoughBatches = doughBatches.filter(
    db => !batchRecordDoughBatches.some(brdb => brdb.doughBatchId === db.id)
  );

  if (loading && batchRecordIngredients.length === 0) {
    return (
      <div
        style={{
          padding: '20px',
          textAlign: 'center',
          color: '#999',
          fontSize: '14px'
        }}
      >
        Loading ingredients...
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          padding: '20px',
          backgroundColor: '#fff3cd',
          color: '#856404',
          borderRadius: '4px',
          border: '1px solid #ffeaa7',
          fontSize: '14px'
        }}
      >
        Error: {error}
      </div>
    );
  }

  return (
    <div>
      {/* Filling Batches Section */}
      {batchRecordFillingBatches.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#555' }}>
            Filling Batches
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {batchRecordFillingBatches.map(brfb => (
              <div
                key={brfb.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 12px',
                  backgroundColor: '#f9f9f9',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              >
                <div>
                  <span style={{ fontWeight: '500', fontSize: '14px' }}>
                    {brfb.fillingProductName}
                  </span>
                  <span style={{ marginLeft: '12px', fontSize: '13px', color: '#666', fontFamily: 'monospace' }}>
                    {brfb.fillingBatchLotNumber}
                  </span>
                  {brfb.fillingDateOfProduction && (
                    <span style={{ marginLeft: '12px', fontSize: '12px', color: '#999' }}>
                      ({new Date(brfb.fillingDateOfProduction).toLocaleDateString()})
                    </span>
                  )}
                </div>
                <button
                  onClick={() => handleRemoveFillingBatch(brfb.fillingBatchId)}
                  style={{
                    padding: '4px 8px',
                    backgroundColor: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Filling Batch Button */}
      {!isAddingFillingBatch && availableFillingBatches.length > 0 && (
        <button
          onClick={() => setIsAddingFillingBatch(true)}
          style={{
            marginTop: batchRecordFillingBatches.length > 0 ? '0px' : '0px',
            marginBottom: '16px',
            padding: '10px 16px',
            backgroundColor: '#2196F3',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <span style={{ fontSize: '16px' }}>+</span>
          Add Filling Batch
        </button>
      )}

      {/* Filling Batch Multi-Select */}
      {isAddingFillingBatch && (
        <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f9f9f9', borderRadius: '4px', border: '1px solid #ddd' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
            Select Filling Batches:
          </label>
          <div
            style={{
              maxHeight: '200px',
              overflowY: 'auto',
              border: '1px solid #ddd',
              borderRadius: '4px',
              padding: '8px',
              backgroundColor: 'white',
              marginBottom: '8px'
            }}
          >
            {fillingBatchesLoading ? (
              <div style={{ padding: '12px', textAlign: 'center', color: '#999' }}>
                Loading filling batches...
              </div>
            ) : availableFillingBatches.length === 0 ? (
              <div style={{ padding: '12px', textAlign: 'center', color: '#999' }}>
                No available filling batches
              </div>
            ) : (
              availableFillingBatches.map(batch => (
                <label
                  key={batch.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '6px 8px',
                    cursor: 'pointer',
                    borderRadius: '4px',
                    marginBottom: '4px',
                    backgroundColor: selectedFillingBatchIds.includes(batch.id) ? '#e3f2fd' : 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    if (!selectedFillingBatchIds.includes(batch.id)) {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!selectedFillingBatchIds.includes(batch.id)) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedFillingBatchIds.includes(batch.id)}
                    onChange={(e) => handleFillingBatchToggle(batch.id, e.target.checked)}
                    style={{
                      marginRight: '8px',
                      cursor: 'pointer'
                    }}
                  />
                  <span style={{ fontSize: '13px', color: '#333' }}>
                    {batch.productName} - {batch.batchLotNumber}
                    {batch.dateOfProduction && (
                      <span style={{ marginLeft: '8px', color: '#666' }}>
                        ({new Date(batch.dateOfProduction).toLocaleDateString()})
                      </span>
                    )}
                  </span>
                </label>
              ))
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={handleAddSelectedFillingBatches}
              disabled={selectedFillingBatchIds.length === 0}
              style={{
                padding: '6px 12px',
                backgroundColor: selectedFillingBatchIds.length === 0 ? '#ccc' : '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: selectedFillingBatchIds.length === 0 ? 'not-allowed' : 'pointer',
                fontSize: '13px'
              }}
            >
              Add Selected ({selectedFillingBatchIds.length})
            </button>
            <button
              onClick={() => {
                setIsAddingFillingBatch(false);
                setSelectedFillingBatchIds([]);
              }}
              style={{
                padding: '6px 12px',
                backgroundColor: '#757575',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Dough Batches Section */}
      {batchRecordDoughBatches.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#555' }}>
            Dough Batches
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {batchRecordDoughBatches.map(brdb => (
              <div
                key={brdb.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 12px',
                  backgroundColor: '#f9f9f9',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              >
                <div>
                  <span style={{ fontWeight: '500', fontSize: '14px' }}>
                    {brdb.doughProductName}
                  </span>
                  <span style={{ marginLeft: '12px', fontSize: '13px', color: '#666', fontFamily: 'monospace' }}>
                    {brdb.doughBatchNumber}
                  </span>
                  {brdb.doughDateOfProduction && (
                    <span style={{ marginLeft: '12px', fontSize: '12px', color: '#999' }}>
                      ({new Date(brdb.doughDateOfProduction).toLocaleDateString()})
                    </span>
                  )}
                </div>
                <button
                  onClick={() => handleRemoveDoughBatch(brdb.doughBatchId)}
                  style={{
                    padding: '4px 8px',
                    backgroundColor: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Dough Batch Button */}
      {!isAddingDoughBatch && availableDoughBatches.length > 0 && (
        <button
          onClick={() => setIsAddingDoughBatch(true)}
          style={{
            marginTop: batchRecordDoughBatches.length > 0 ? '0px' : '0px',
            marginBottom: '16px',
            padding: '10px 16px',
            backgroundColor: '#FF9800',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <span style={{ fontSize: '16px' }}>+</span>
          Add Dough Batch
        </button>
      )}

      {/* Dough Batch Multi-Select */}
      {isAddingDoughBatch && (
        <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fff8e1', borderRadius: '4px', border: '1px solid #ffe082' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
            Select Dough Batches:
          </label>
          <div
            style={{
              maxHeight: '200px',
              overflowY: 'auto',
              border: '1px solid #ffe082',
              borderRadius: '4px',
              padding: '8px',
              backgroundColor: 'white',
              marginBottom: '8px'
            }}
          >
            {doughBatchesLoading ? (
              <div style={{ padding: '12px', textAlign: 'center', color: '#999' }}>
                Loading dough batches...
              </div>
            ) : availableDoughBatches.length === 0 ? (
              <div style={{ padding: '12px', textAlign: 'center', color: '#999' }}>
                No available dough batches
              </div>
            ) : (
              availableDoughBatches.map(batch => (
                <label
                  key={batch.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '6px 8px',
                    cursor: 'pointer',
                    borderRadius: '4px',
                    marginBottom: '4px',
                    backgroundColor: selectedDoughBatchIds.includes(batch.id) ? '#fff3e0' : 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    if (!selectedDoughBatchIds.includes(batch.id)) {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!selectedDoughBatchIds.includes(batch.id)) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedDoughBatchIds.includes(batch.id)}
                    onChange={(e) => handleDoughBatchToggle(batch.id, e.target.checked)}
                    style={{
                      marginRight: '8px',
                      cursor: 'pointer'
                    }}
                  />
                  <span style={{ fontSize: '13px', color: '#333' }}>
                    {batch.productName} - {batch.batchNumber}
                    {batch.dateOfProduction && (
                      <span style={{ marginLeft: '8px', color: '#666' }}>
                        ({new Date(batch.dateOfProduction).toLocaleDateString()})
                      </span>
                    )}
                  </span>
                </label>
              ))
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={handleAddSelectedDoughBatches}
              disabled={selectedDoughBatchIds.length === 0}
              style={{
                padding: '6px 12px',
                backgroundColor: selectedDoughBatchIds.length === 0 ? '#ccc' : '#FF9800',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: selectedDoughBatchIds.length === 0 ? 'not-allowed' : 'pointer',
                fontSize: '13px'
              }}
            >
              Add Selected ({selectedDoughBatchIds.length})
            </button>
            <button
              onClick={() => {
                setIsAddingDoughBatch(false);
                setSelectedDoughBatchIds([]);
              }}
              style={{
                padding: '6px 12px',
                backgroundColor: '#757575',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', marginBottom: '16px' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse'
          }}
        >
        <thead>
          <tr
            style={{
              backgroundColor: '#f8f9fa',
              borderBottom: '2px solid #e0e0e0'
            }}
          >
            <th
              style={{
                padding: '12px',
                textAlign: 'left',
                fontWeight: '600',
                color: '#555',
                fontSize: '13px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              Ingredient Name
            </th>
            <th
              style={{
                padding: '12px',
                textAlign: 'left',
                fontWeight: '600',
                color: '#555',
                fontSize: '13px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              Lot Numbers
            </th>
            <th
              style={{
                padding: '12px',
                textAlign: 'left',
                fontWeight: '600',
                color: '#555',
                fontSize: '13px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                width: '120px'
              }}
            >
              Quantity
            </th>
            <th
              style={{
                padding: '12px',
                textAlign: 'left',
                fontWeight: '600',
                color: '#555',
                fontSize: '13px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                width: '100px'
              }}
            >
              Unit
            </th>
            <th
              style={{
                padding: '12px',
                textAlign: 'center',
                fontWeight: '600',
                color: '#555',
                fontSize: '13px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                width: '100px'
              }}
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {batchRecordIngredients.map(ingredient => (
            <BatchRecordIngredientRow
              key={ingredient.id}
              ingredient={ingredient}
              onEdit={handleEditIngredient}
              onDelete={handleDelete}
            />
          ))}
          {isAddingNew && (
            <tr style={{ backgroundColor: '#f9f9f9' }}>
              <td style={{ padding: '12px' }}>
                <select
                  value={newIngredientName || ''}
                  onChange={(e) => {
                    console.log('[BatchRecordIngredientsTable] Ingredient selected:', e.target.value);
                    setNewIngredientName(e.target.value);
                    setNewLotNumber(null);
                    console.log('[BatchRecordIngredientsTable] Reset lot number to null');
                  }}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Select ingredient...</option>
                  {getUniqueIngredientNames().map(name => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </td>
              <td style={{ padding: '12px' }}>
                {!newIngredientName ? (
                  <div
                    style={{
                      padding: '12px',
                      backgroundColor: '#f5f5f5',
                      borderRadius: '4px',
                      color: '#999',
                      fontSize: '13px',
                      fontStyle: 'italic',
                      textAlign: 'center'
                    }}
                  >
                    Select an ingredient first
                  </div>
                ) : getAvailableLots(newIngredientName).length === 0 ? (
                  <div
                    style={{
                      padding: '12px',
                      backgroundColor: '#fff3cd',
                      color: '#856404',
                      borderRadius: '4px',
                      fontSize: '13px',
                      fontWeight: '500'
                    }}
                  >
                    ⚠️ No lots available for this ingredient
                  </div>
                ) : (
                  <div
                    style={{
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      padding: '8px',
                      maxHeight: '120px',
                      overflowY: 'auto',
                      backgroundColor: '#fafafa'
                    }}
                  >
                    {getAvailableLots(newIngredientName).map(lot => {
                      const isSelected = newLotNumber === lot;
                      return (
                        <label
                          key={lot}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '6px 8px',
                            cursor: 'pointer',
                            borderRadius: '4px',
                            marginBottom: '4px',
                            backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.backgroundColor = '#f5f5f5';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }
                          }}
                        >
                          <input
                            type="radio"
                            name="lotSelection"
                            checked={isSelected}
                            onChange={() => {
                              console.log('[BatchRecordIngredientsTable] Radio button selected for lot:', lot);
                              setNewLotNumber(lot);
                            }}
                            style={{
                              marginRight: '8px',
                              cursor: 'pointer'
                            }}
                          />
                          <span style={{ fontSize: '13px', color: '#333', fontFamily: 'monospace' }}>
                            {lot}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
                <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                  {newIngredientName && getAvailableLots(newIngredientName).length > 0 && newLotNumber && (
                    <>
                      1 lot selected
                    </>
                  )}
                </div>
              </td>
              <td style={{ padding: '12px' }}>
                <input
                  type="number"
                  value={newQuantity}
                  onChange={(e) => {
                    console.log('[BatchRecordIngredientsTable] Quantity changed to:', e.target.value);
                    setNewQuantity(e.target.value);
                  }}
                  placeholder="Optional"
                  step="0.01"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </td>
              <td style={{ padding: '12px' }}>
                <select
                  value={newUnit}
                  onChange={(e) => {
                    console.log('[BatchRecordIngredientsTable] Unit changed to:', e.target.value);
                    setNewUnit(e.target.value);
                  }}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Optional...</option>
                  <option value="kg">kg</option>
                  <option value="g">g</option>
                  <option value="lb">lb</option>
                  <option value="oz">oz</option>
                  <option value="L">L</option>
                  <option value="mL">mL</option>
                  <option value="gal">gal</option>
                  <option value="qt">qt</option>
                  <option value="pt">pt</option>
                  <option value="cup">cup</option>
                  <option value="tbsp">tbsp</option>
                  <option value="tsp">tsp</option>
                  <option value="unit">unit</option>
                </select>
              </td>
              <td style={{ padding: '12px', textAlign: 'center' }}>
                <button
                  onClick={handleSaveNew}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    marginRight: '4px'
                  }}
                >
                  Save
                </button>
                <button
                  onClick={handleCancelAdd}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#757575',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  Cancel
                </button>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </div>

      {!isAddingNew && (
        <button
          onClick={handleAddIngredient}
          style={{
            padding: '10px 16px',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <span style={{ fontSize: '16px' }}>+</span>
          Add Ingredient
        </button>
      )}

      {batchRecordIngredients.length === 0 && !isAddingNew && (
        <div
          style={{
            padding: '20px',
            textAlign: 'center',
            color: '#999',
            fontSize: '14px',
            fontStyle: 'italic'
          }}
        >
          No ingredients added yet. Click "Add Ingredient" to get started.
        </div>
      )}

      {editingIngredient && (
        <BatchRecordIngredientModal
          ingredient={editingIngredient}
          rawIngredients={rawIngredients}
          onSave={handleSaveEdit}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};
